import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Container, Link } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Page from 'src/components/Page';
import { useMailOrbitFeature } from 'src/utils/mailorbitFeature';
import {
  createWorkOrderFromConversation,
  getConversation,
  getWorkOrderLink,
  getMailOrbitErrorKey,
  isMailOrbitMock,
} from 'src/utils/MailOrbitService';
import ConversationView from 'src/components/MailOrbit/WorkOrderEmail/ConversationView';
import BackToList from 'src/components/MailOrbit/WorkOrderEmail/BackToList';
import useStyles from 'src/components/MailOrbit/WorkOrderEmail/useStyles';
import { openWorkOrder, WORK_ORDER_NOT_ACCESSIBLE_KEY } from 'src/components/MailOrbit/openWorkOrder';
import { closedWorkOrderId, openWorkOrderId } from './linkedWorkOrder';

/**
 * Iş emri olmadan MailOrbit conversation görünümü (SR-024) + "İş emri aç".
 * Kapı kapalıyken hiçbir şey çizilmez ve istek atılmaz (SR-019 kapısı).
 */
const Content = () => {
  const { conversationId } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const { t } = useTranslation(['mailorbitManual', 'mailorbit']);
  const [detail, setDetail] = useState(null);
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [mockCreated, setMockCreated] = useState(null);
  const [openFailed, setOpenFailed] = useState(false);
  const lock = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorKey(null);
    setDetail(null);
    setLink(null);
    Promise.all([
      getConversation(conversationId, { includeBodies: false }),
      getWorkOrderLink(conversationId).catch(() => null), // hata: bagli is emri yokmus gibi
    ])
      .then(([res, lnk]) => {
        if (cancelled) return;
        setDetail(res);
        setLink(lnk);
      })
      .catch(e => !cancelled && setErrorKey(getMailOrbitErrorKey(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  const handleCreate = useCallback(async newIfClosed => {
    if (lock.current) return; // çift tıklama kilidi (state güncellemesinden önce de geçerli)
    lock.current = true;
    setCreating(true);
    setCreateError(null);
    try {
      const res = await createWorkOrderFromConversation(conversationId, { newIfClosed: newIfClosed === true });
      if (isMailOrbitMock()) {
        // Mock modda gerçek iş emri ekranı olmadığı için yönlendirme yapılmaz.
        setMockCreated(res.workOrderId);
        return;
      }
      // Email2Case on kosullariyla is emri sayfasi (E-mail sekmesi); erisilemezse mesaj, yonlendirme yok.
      if (!(await openWorkOrder(history, res.workOrderId))) setOpenFailed(true);
    } catch (e) {
      setCreateError(getMailOrbitErrorKey(e));
    } finally {
      lock.current = false;
      setCreating(false);
    }
  }, [conversationId, history]);

  const linkedId = openWorkOrderId(link);

  // Acik bagli is emri varsa: Email2Case on kosullariyla is emri sayfasina gec.
  useEffect(() => {
    if (loading || errorKey || !linkedId) return undefined;
    let cancelled = false;
    openWorkOrder(history, linkedId).then(ok => {
      if (!ok && !cancelled) setOpenFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [loading, errorKey, linkedId, history]);

  if (loading) {
    return (
      <Box className={classes.center}>
        <CircularProgress />
      </Box>
    );
  }
  if (errorKey) {
    return (
      <Box className={classes.center}>
        <Alert severity="error">{t(`mailorbit:${errorKey}`)}</Alert>
      </Box>
    );
  }

  if (linkedId && !openFailed) {
    return (
      <Box className={classes.center}>
        <CircularProgress />
      </Box>
    );
  }
  const closedId = closedWorkOrderId(link);

  const canCreate = !!(detail && detail.canWork);

  return (
    <Container maxWidth={false} style={{ paddingTop: 8, paddingBottom: 16 }}>
      <BackToList preferBack current={(detail && detail.caseReference) || ''} />
      <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 12, marginBottom: 12 }}>
        {closedId ? (
          <Alert severity="info" style={{ flex: 1, minWidth: 240 }}>
            {t('mailorbitManual:work_order_closed', { id: closedId })}{' '}
            <Link
              component="button"
              type="button"
              style={{ verticalAlign: 'baseline' }}
              onClick={() => {
                setOpenFailed(false);
                openWorkOrder(history, closedId).then(ok => !ok && setOpenFailed(true));
              }}
            >
              {t('mailorbitManual:go_to_closed')}
            </Link>
          </Alert>
        ) : (
          <Alert severity="info" style={{ flex: 1, minWidth: 240 }}>
            {t('mailorbitManual:no_work_order')}
          </Alert>
        )}
        {canCreate ? (
          <Button
            variant="contained"
            color="primary"
            disabled={creating}
            onClick={() => handleCreate(!!closedId)}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {creating
              ? t('mailorbitManual:creating')
              : t(closedId ? 'mailorbitManual:create_new_work_order' : 'mailorbitManual:create_work_order')}
          </Button>
        ) : null}
      </Box>
      {!canCreate ? (
        <Alert severity="warning" style={{ marginBottom: 12 }}>
          {t('mailorbitManual:cannot_work')}
        </Alert>
      ) : null}
      {openFailed ? (
        <Alert severity="error" style={{ marginBottom: 12 }}>
          {t(WORK_ORDER_NOT_ACCESSIBLE_KEY)}
        </Alert>
      ) : null}
      {createError ? (
        <Alert severity="error" style={{ marginBottom: 12 }}>
          {t(`mailorbit:${createError}`)}
        </Alert>
      ) : null}
      {mockCreated ? (
        <Alert severity="success" style={{ marginBottom: 12 }}>
          {t('mailorbitManual:mock_created', { id: mockCreated })}
        </Alert>
      ) : null}
      <Box className={classes.root}>
        <ConversationView
          key={conversationId}
          conversationId={conversationId}
          allowTerminalStatuses={!linkedId}
        />
      </Box>
    </Container>
  );
};

const StandaloneConversation = () => {
  const { t } = useTranslation('mailorbitManual');
  const { enabled, loaded } = useMailOrbitFeature();
  if (!loaded) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }
  if (!enabled) return <Redirect to="/overview" />;
  return (
    <Page title={t('page_title')}>
      <Content />
    </Page>
  );
};

export default StandaloneConversation;
